import type { SupabaseClient } from '@supabase/supabase-js';

type SessionClient = Pick<SupabaseClient, 'auth'>;

export interface DedicatedTravelerSession {
  access_token: string;
  refresh_token: string;
  user: { id: string; role?: string };
}

/** Sharing never replaces an independently authenticated browser identity. */
export async function installTravelerSession(
  client: SessionClient,
  session: DedicatedTravelerSession,
): Promise<boolean> {
  // The backend enforces eligibility. Do not accept legacy responses that
  // identify a regular account instead of a dedicated traveler.
  if (session.user.role !== 'traveler') return false;

  try {
    const { data: { session: current }, error: sessionError } = await client.auth.getSession();
    if (sessionError) return false;
    if (current) {
      // Preserve agency users, platform admins without profiles, and existing
      // traveler logins. A code can change itinerary access, never browser identity.
      // The same dedicated traveler already has a usable session; do not replace it.
      return current.user.id === session.user.id;
    }

    const { error } = await client.auth.setSession({
      access_token: session.access_token,
      refresh_token: session.refresh_token,
    });
    return !error;
  } catch {
    // Cookie-based itinerary/document viewing remains available.
    return false;
  }
}
